import { inject } from 'tsyringe'
import { containerScoped } from '~/decorators/dependency-container'
import RequestBuilder from '~/builders/http/RequestBuilder'
import { XmlDealer, XmlField, XmlOptionsSchema } from '~/models/xml/types'
import { CategoryId } from '~/models/category/types'
import { toCamelCase } from '~/utils/object'
import { toSnakeCase } from '~/utils/snake-case'

@containerScoped()
export default class XmlService {
  constructor(@inject(RequestBuilder) private requestBuilder: RequestBuilder) {}

  public fetchFields(): Promise<XmlField[]> {
    return this.requestBuilder
      .request('get', '/api/xmldoc/part_xyma/fields')
      .validate(body => body?.data?.fields)
      .send()
  }

  public fetchSocketUrl(url: string): Promise<{ relayUrl: string }> {
    return this.requestBuilder
      .request('post', '/api/xml/validate/socket/')
      .data({ url })
      .validate(body => body.data.relay_url)
      .send()
  }

  public validateXml(url: string): Promise<XmlField[]> {
    return this.requestBuilder
      .request('post', '/api/xml/validate/')
      .data({ url })
      .validate(body => body)
      .send()
  }

  public fetchVehiclesFields(category: CategoryId): Promise<XmlField[]> {
    return this.requestBuilder
      .request('get', `/api/xmldoc/${category}/fields/`)
      .validate(body => body?.data)
      .send()
  }

  public fetchVehicleCategories(): Promise<XmlField[]> {
    return this.requestBuilder
      .request('get', '/api/xmldoc/vehicle_categories')
      .validate(body => body?.data)
      .map(body =>
        toCamelCase(
          body.data.map((d: any) => ({
            ...d,
            label: `${d.name}, category_id=${d.id}`
          }))
        )
      )
      .send()
  }

  public fetchMakeIds(text: string = ''): Promise<XmlField[]> {
    return this.requestBuilder
      .request('get', '/api/xmldoc/make_ids/')
      .params({ q: text })
      .validate(body => body?.data)
      .previousRequestCancelable()
      .map(body =>
        toCamelCase(
          body.data.map((d: any) => ({
            ...d,
            label: `${d.make_name}, make_id=${d.make_id}`
          }))
        )
      )
      .send()
  }

  public fetchModelIds(text: string = ''): Promise<XmlField[]> {
    return this.requestBuilder
      .request('get', '/api/xmldoc/model_ids/')
      .params({ q: text })
      .validate(body => body?.data)
      .previousRequestCancelable()
      .map(body =>
        toCamelCase(
          body.data.map((d: any) => ({
            ...d,
            label: `${d.model_name}, model_id=${d.model_id}, make_id=${d.make_id}, vtype=${d.model_vtype}`
          }))
        )
      )
      .send()
  }

  public fetchFeature(vType: string): Promise<XmlField[]> {
    return this.requestBuilder
      .request('get', `/api/xmldoc/features/${vType}/`)
      .validate(body => body?.data)
      .map(body =>
        toCamelCase(
          body.data.map((d: any) => ({
            ...d,
            label: `${d.feature_name}, vtype=${vType},  id=${d.feature_id}`
          }))
        )
      )
      .send()
  }

  public fetchCertificateProviders(): Promise<XmlField[]> {
    return this.requestBuilder
      .request('get', '/api/xmldoc/certificate_providers')
      .validate(body => body?.data)
      .map(body =>
        toCamelCase(
          body.data.map((d: any) => ({
            ...d,
            label: `${d.provider_name}, id=${d.provider_id}`
          }))
        )
      )
      .send()
  }

  public fetchLeasingExtras(): Promise<XmlField[]> {
    return this.requestBuilder
      .request('get', '/api/xmldoc/leasing_extras')
      .validate(body => body?.data)
      .map(body =>
        toCamelCase(
          body.data.map((d: any) => ({
            ...d,
            label: `${d.name}, id=${d.id}`
          }))
        )
      )
      .send()
  }

  fetchXml(): Promise<XmlDealer[]> {
    return this.requestBuilder
      .request('GET', '/api/managers/xml/')
      .validate(body => body.data?.dealers)
      .send()
  }

  fetchSchema(dealerId: number): Promise<XmlOptionsSchema> {
    return this.requestBuilder
      .request('GET', `/api/managers/dealers/${dealerId}/xml-options`)
      .validate(body => body.data)
      .send()
  }

  checkXml(dealerId: number): Promise<XmlField[]> {
    return this.requestBuilder
      .request('post', '/api/xml/validate/')
      .data({ dealer_id: dealerId })
      .validate(body => body)
      .send()
  }

  patchXmlOptions(
    dealer: XmlDealer
  ): Promise<{
    values: {
      xmlEmail: string
      xmlType: string
      xmlUrl: string
    }
  }> {
    return this.requestBuilder
      .request('patch', `/api/managers/dealers/${dealer.id}/xml-options`)
      .data(toSnakeCase(dealer))
      .validate(body => body)
      .send()
  }
}
